import React, { useCallback } from 'react';

import { Col, Container, Row } from 'react-grid-system';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DepButton } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import { addNotification } from '@app/store/actions/notification';
import { getCurrentUserEmojis } from '@app/store/zustand/emojis/useEmojisStore';

import YatShareModule from '../../Dashboard/ShareModule';
import { ReactComponent as CheckIcon } from '../icons/check-white-round.svg';
import { ReactComponent as FloppyDiskIcon } from '../icons/floppy-disk-white.svg';
import { ReactComponent as WarningIcon } from '../icons/warning-triangle-white.svg';

import './_YatPageNavigation.scss';

type Props = {
    emojiID: EmojiId;
    isDirty: boolean;
    editable: boolean;
    invalidData: boolean;
    saveChanges: () => Promise<boolean>;
};

export default function YatPageNavigation({ emojiID, saveChanges, isDirty, invalidData, editable }: Props) {
    const { t } = useTranslation(['yat_page', 'translation']);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const emojiId = useAppSelector((state) => state.user.primaryYat);

    const onComplete = useCallback(() => {
        if (isDirty) {
            // The timeout is here to allow for the focus timeout to complete and set the data.
            setTimeout(() => {
                saveChanges().then((success: boolean) => {
                    if (success) {
                        getCurrentUserEmojis(dispatch).then(() => {
                            navigate(`/${emojiId}/yats`);
                        });
                    }
                });
            }, 10);
        } else {
            navigate(`/${emojiId}/yats`);
        }
    }, [dispatch, isDirty, navigate, saveChanges, emojiId]);

    function handleClick() {
        if (editable && invalidData) {
            dispatch(
                addNotification({
                    title: t('link_notification_title'),
                    message: t('link_notification_body'),
                    severity: 'alert',
                }),
            );
        } else {
            onComplete();
        }
    }
    const buttonIcon = invalidData ? <WarningIcon /> : isDirty ? <FloppyDiskIcon /> : <CheckIcon />;
    const buttonCopy = isDirty || invalidData ? t('done_editing') : t('done');

    return (
        // No gutter as this is a nested row
        <Row className="yat-page-navigation v2-global-reset" align="center" nogutter={true}>
            <Col>
                <Container>
                    <Row align="center">
                        <Col>
                            <DepButton variant="primary" glow size="small" onClick={() => handleClick()}>
                                {buttonIcon}
                                {buttonCopy}
                            </DepButton>
                        </Col>

                        <Col>
                            <YatShareModule yat={emojiID} classes="yat-page-navigation__share-module" />
                        </Col>
                    </Row>
                </Container>
            </Col>
        </Row>
    );
}
